import React, { Suspense, useEffect, useState } from "react";
import { Route, Switch, Redirect, useLocation } from "react-router-dom";
import { authorizedRoutes, authorizedMobileRoutes } from "./mapping";
import DataWrapper from "components/AppLayout/DataWrapper";
import AppLayout from "components/AppLayout/AppLayout";
import loading from "assets/images/infinite-blue.svg";
import styles from "components/AppLayout/applayout.module.css";
import QuickSmsForm from "components/QuickSms/QuickSmsForm";
import QuickSmsWrapper from "components/QuickSms/QuickSmsWrapper";
import { Row, Col } from "antd";
import { observer, inject } from "mobx-react";
import TabsMenu from "Mobile/Pages/TabsMenu";
import {
  addTawkScript,
  addLiveChatScript,
  setTawkAttributes,
  removeGoogleTagManagerScript,
  removeLinkedInScript,
  removeTawkScript,
  removeApolloScript,
  removeClarityScript,
  addClarityScript
} from "scripts";
import { Helmet } from "react-helmet";
// import SaleCountDown from "components/BuyCredits/SaleCountDown";

function getWindowWidth() {
  const { innerWidth: width } = window;
  return {
    width
  };
}

function useWindowWidth() {
  const [windowWidth, setWindowWidth] = useState(getWindowWidth());
  useEffect(() => {
    function handleResize() {
      setWindowWidth(getWindowWidth());
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return windowWidth;
}

function isAdminRoute(path) {
  const adminRoutes = ["/admin", "/admin/dashboard", "/admin/users", "/admin/settings"];

  return adminRoutes.some(adminRoute => path.startsWith(adminRoute) || path === adminRoute);
}

function hasAdminAccess(user) {
  return user && user.role === "superAdmin";
}

function isAuthLoading(authStore) {
  return !authStore || authStore.isLoading === true;
}

function Authorized({ modalStore, authStore }) {
  const { width } = useWindowWidth();
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [spanNo, setSpanNo] = useState(24);
  const location = useLocation();

  if (isAuthLoading(authStore)) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: "100vh" }}>
        <img src={loading} alt="loading" width="100px" />
      </div>
    );
  }

  useEffect(() => {
    if (modalStore.quickMessage && width > 1200) {
      setSpanNo(17);
    } else if (modalStore.quickMessage && width > 992 && width < 1200) {
      setSpanNo(14);
    } else {
      setSpanNo(24);
    }
  }, [modalStore.quickMessage, width]);

  useEffect(() => {
    if (width < 768) {
      setIsSmallScreen(true);
    } else {
      setIsSmallScreen(false);
    }
  }, [width]);

  useEffect(() => {
    if (authStore && authStore.user !== undefined) {
      if (isAdminRoute(location.pathname) && !hasAdminAccess(authStore.user)) {
        console.error("User attempted to access admin route without permission");
      }
    }
  }, [location.pathname, authStore?.user]);

  // Whenever the authenticated user changes, set their attributes on Tawk (name/email)
  useEffect(() => {
    try {
      if (authStore?.user) setTawkAttributes(authStore.user);
    } catch (err) {
      console.warn("setTawkAttributes call failed:", err);
    }
  }, [authStore?.user]);

  return (
    <>
      {isSmallScreen ? (
        <Suspense
          fallback={
            <div className="d-flex align-items-center justify-content-center" style={{ height: "100vh" }}>
              <img src={loading} alt="loading" width="100px" />
            </div>
          }
        >
          <Switch>
            {authorizedMobileRoutes.map(i => (
              <Route
                key={i.path}
                exact={i.exact}
                path={i.path}
                render={props => {
                  if (authStore?.user !== undefined && isAdminRoute(i.path) && !hasAdminAccess(authStore.user)) {
                    return <Redirect to="/" />;
                  }

                  removeApolloScript();
                  const pathname = props.location.pathname;
                  if (pathname.startsWith("/useroptout/") || pathname.startsWith("/optout/")) {
                    removeTawkScript();
                    removeClarityScript();
                  } else {
                    // Clarity script only for dashboard route
                    if (i.path === "/") {
                      addClarityScript(true);
                    } else {
                      removeClarityScript();
                    }
                    // removeLiveChatScript();
                    // removeHubspotScript();
                    removeGoogleTagManagerScript();
                    removeLinkedInScript();
                  }
                  !i?.stopPreview ? (document.title = `${i.title ? `${i.title} | ` : ""}Guni Online and Bulk SMS Gateway`) : null;
                  return (
                    <DataWrapper>
                      {i?.stopPreview && (
                        <Helmet>
                          <title> </title>
                          <meta property="og:url" content="https://app.gunisms.com.au/nonexistent-url" />
                        </Helmet>
                      )}
                      {/* <SaleCountDown /> */}
                      {i.NavBar && <i.NavBar />}
                      <TabsMenu>
                        <i.Component {...props} />
                      </TabsMenu>
                    </DataWrapper>
                  );
                }}
              />
            ))}
          </Switch>
        </Suspense>
      ) : (
        <Suspense
          fallback={
            <div className="d-flex">
              <div className={`${localStorage.getItem("isSidebarOpen") === "false" ? "" : styles.active} ${styles.sidebar}`}>
                <div className={styles.logo}>
                  <div className={`rounded-pill ${styles.text} ${localStorage.getItem("isSidebarOpen") === "false" ? "" : styles.big}`} />
                </div>
              </div>
            </div>
          }
        >
          <Switch>
            {authorizedRoutes.map(i => (
              <Route
                key={i.path}
                exact={i.exact}
                path={i.path}
                render={props => {
                  if (authStore?.user !== undefined && isAdminRoute(i.path) && !hasAdminAccess(authStore.user)) {
                    return <Redirect to="/" />;
                  }

                  removeApolloScript();
                  document.title = `${i.title ? `${i.title} | ` : ""}Guni Online and Bulk SMS Gateway`;
                  const pathname = props.location.pathname;
                  if (pathname.startsWith("/useroptout/") || pathname.startsWith("/optout/")) {
                    removeTawkScript();
                    removeClarityScript();
                  } else {
                    // Clarity script only for dashboard route
                    if (i.path === "/") {
                      addClarityScript(true);
                    } else {
                      removeClarityScript();
                    }
                    addLiveChatScript();
                    // Replace previous chat widget with Tawk
                    addTawkScript();
                    // If we have a logged-in user, set their name/email on Tawk
                    try {
                      setTawkAttributes(authStore?.user || {});
                    } catch (err) {
                      console.warn("setTawkAttributes failed:", err);
                    }
                    // removeHubspotScript();
                    removeGoogleTagManagerScript();
                    removeLinkedInScript();
                  }
                  if (i?.removeSideBar) return <i.Component {...props} {...i.customProps} width={width} />;
                  !i?.stopPreview ? (document.title = `${i.title ? `${i.title} | ` : ""}Guni Online and Bulk SMS Gateway`) : null;
                  return (
                    <DataWrapper>
                      {i?.stopPreview && (
                        <Helmet>
                          <title> </title>
                          <meta property="og:url" content="https://app.gunisms.com.au/nonexistent-url" />
                        </Helmet>
                      )}
                      <AppLayout retailChatSideBar={i?.retailChatSideBar}>
                        <div id="page-content-wrapper">
                          {i.NavBar && <i.NavBar />}
                          <Row gutter={20} className="px-3">
                            <Col span={24} lg={spanNo} xl={spanNo}>
                              <i.Component {...props} {...i.customProps} width={width} />
                            </Col>
                            <QuickSmsWrapper>
                              <QuickSmsForm />
                            </QuickSmsWrapper>
                          </Row>
                        </div>
                      </AppLayout>
                    </DataWrapper>
                  );
                }}
              />
            ))}
          </Switch>
        </Suspense>
      )}
    </>
  );
}

export default inject(stores => ({
  modalStore: stores.store.modalStore,
  authStore: stores.store.authStore
}))(observer(Authorized));
